import { mergeStyles } from '@fluentui/react';
import * as React from 'react';
import {
  LocalizedDaysOfWeek,
  DayOfWeek,
  BusinessHourData,
} from '../../../models/apiModels';
import { getContactUsClassNames, getContactUsStyles } from '../styles/contactUsStyles';
import { IContactUsCurrentTheme } from '../styles/theme.model';

export interface HoursOfOperationProps {
  hoursOfOperationText: string;
  localizedDaysOfWeek: LocalizedDaysOfWeek;
  businessHours: Array<BusinessHourData>;
  hoursOfOperationTimeZoneMessage: string;
  theme: IContactUsCurrentTheme;
}

const getTimeText = (hour: number, min: number): string => {
  if (!(0 <= hour && hour < 24 && 0 <= min && min < 60)) {
      return '';
  }

  return `${hour < 10 ? '0' : ''}${hour}:${min < 10 ? '0' : ''}${min}`;
};

const getDayText = (localizedDaysOfWeek: LocalizedDaysOfWeek, day: number): string => {
  if (!(!!localizedDaysOfWeek && !!localizedDaysOfWeek.DaysOfWeek && 0 <= day && day < 7)) {
    return '';
  }

  return localizedDaysOfWeek.DaysOfWeek[DayOfWeek[day]];
};

interface HoursOfOperationLineProps {
  theme: IContactUsCurrentTheme;
  localizedDaysOfWeek: LocalizedDaysOfWeek,
  bh: BusinessHourData
}

const HoursOfOperationLine = (props: HoursOfOperationLineProps): JSX.Element => {
  const key = props.bh.StartDayOfWeek;
  const contactUsClassNames = getContactUsClassNames(getContactUsStyles, { theme: props.theme });
  let dayText = '';

  if (props.bh.StartDayOfWeek === props.bh.EndDayOfWeek) {
    dayText = getDayText(props.localizedDaysOfWeek, props.bh.StartDayOfWeek);
  } else {
    dayText = `${getDayText(props.localizedDaysOfWeek, props.bh.StartDayOfWeek)} - ${getDayText(props.localizedDaysOfWeek, props.bh.EndDayOfWeek)}`;
  }

  const timeText = `${getTimeText(props.bh.StartHour, props.bh.StartMin)} - ${getTimeText(props.bh.EndHour, props.bh.EndMin)}`;

  return (
    <div key={key} className={contactUsClassNames.dynamicHoopsLine}>
      <span className={contactUsClassNames.dayOfWeek}>{`${dayText}: `}</span>
      <span>{timeText}</span>
    </div>
  );
};

const getTimeZoneText = (bh: BusinessHourData): string => {
  if (!(!!bh && !!bh.TimeZoneName && !!bh.UtcOffset)) {
    return '';
  }

  const utcOffset = bh.UtcOffset;
  const hasSignChar = utcOffset.startsWith('+') || utcOffset.startsWith('-');
  const isPositive = !utcOffset.startsWith('-');
  const lastColonIndex = utcOffset.lastIndexOf(':');
  return `(UTC${isPositive ? '+' : '-'}${utcOffset.substring(hasSignChar ? 1 : 0, lastColonIndex)}) ${bh.TimeZoneName}`;
};

export const HoursOfOperation = (props: HoursOfOperationProps) => {
  const contactUsClassNames = getContactUsClassNames(getContactUsStyles, { theme: props.theme });
  return (
    !!props.businessHours &&
    props.businessHours.length > 0 &&
    (
      <div className={mergeStyles('col-lg-12', contactUsClassNames.col, contactUsClassNames.bodyCopySmall, contactUsClassNames.wrapText)}>
        <div>{props.hoursOfOperationText}</div>
        <div>
          {
            props.businessHours.map((bh: BusinessHourData) => {
              return <HoursOfOperationLine localizedDaysOfWeek={props.localizedDaysOfWeek} bh={bh} theme={props.theme} />;
            })
          }
        </div>
        <div>{props.hoursOfOperationTimeZoneMessage.replace('{0}', `${getTimeZoneText(props.businessHours[0])}`)}</div>
      </div>
    )
  );
};
